package co.uk.paxes.pinpoint.model

import co.uk.paxes.pinpoint.DebugLogger
import co.uk.paxes.pinpoint.util.SeededRandom
import kotlin.random.Random

/**
 * PINPOINT board with 9 sections (3x3 grid of sections)
 * Each section contains up to 16 pin positions (4x4 grid within section)
 * Total: 100 positions for authentic 10x10 PINPOINT board
 */
@kotlinx.serialization.Serializable
data class Board private constructor(
    val sections: Array<Array<Section>>,
    val boardSeed: Long? = null  // The seed used to create this board (for dress pins)
) {
    companion object {
        // Fixed dress pin positions (legacy coordinates)
        private val DRESS_PIN_POSITIONS = listOf(
            Position.fromLegacy(3, 3),
            Position.fromLegacy(3, 6), 
            Position.fromLegacy(6, 3),
            Position.fromLegacy(6, 6)
        )
        private val DRESS_COLORS = listOf(PinColor.RED, PinColor.GREEN, PinColor.PURPLE, PinColor.YELLOW)
        
        /**
         * Create a new Board with dress pins initialized.
         * @param randomSeed Optional seed for deterministic dress pin colors
         */
        operator fun invoke(
            initializeDressPins: Boolean = true,
            randomSeed: Long? = null
        ): Board {
            DebugLogger.d("Board", "Board.invoke START: initializeDressPins=$initializeDressPins, randomSeed=$randomSeed")
            
            // Create empty sections first
            val sections = Array(3) { sectionRow ->
                Array(3) { sectionCol ->
                    Section(sectionRow, sectionCol)
                }
            }
            
            if (initializeDressPins) {
                DebugLogger.d("Board", "Board.invoke: WILL initialize dress pins")
                
                val shuffledColors = if (randomSeed != null) {
                    SeededRandom.shuffleWithSeed(DRESS_COLORS, randomSeed)
                } else {
                    DRESS_COLORS.shuffled()
                }
                
                // Place dress pins by creating new Section objects with pins
                DRESS_PIN_POSITIONS.forEachIndexed { index, position ->
                    val dressPin = Pin.dress(shuffledColors[index])
                    // Create a new Section with the dress pin placed
                    sections[position.sectionRow][position.sectionCol] = 
                        sections[position.sectionRow][position.sectionCol]
                            .placePinAt(position.localRow, position.localCol, dressPin)
                }
                
                if (randomSeed != null) {
                    DebugLogger.d("Board", "Board initialized with seed: $randomSeed")
                }
                DebugLogger.d("Board", "Dress pins placed - Colors: ${shuffledColors.map { it.name }}")
                
                // Verify dress pins were placed
                val pinCount = sections.sumOf { row -> row.sumOf { section -> section.getOccupiedPositions().size } }
                DebugLogger.d("Board", "Board.invoke: Total pins after dress pin placement: $pinCount")
            } else {
                DebugLogger.d("Board", "Board.invoke: SKIPPING dress pins (network client)")
            }
            
            DebugLogger.d("Board", "Board.invoke END")
            return Board(sections, boardSeed = randomSeed)
        }
        
        /**
         * Create a Board from existing sections (no dress pin initialization).
         * Used for deserialization and board updates.
         */
        fun fromSections(sections: Array<Array<Section>>, seed: Long? = null): Board {
            return Board(sections, boardSeed = seed)
        }
        
        /**
         * Create a Board with a specific seed for dress pins.
         */
        fun createWithSeed(seed: Long): Board {
            return invoke(initializeDressPins = true, randomSeed = seed)
        }
    }
    
    fun getPinAt(position: Position): Pin? {
        return sections[position.sectionRow][position.sectionCol]
            .getPinAt(position.localRow, position.localCol)
    }
    
    fun isEmpty(position: Position): Boolean = getPinAt(position) == null

    fun isValidMove(position: Position): Boolean {
        // Basic check: position is on board and empty
        // Since Position handles bounds (0-9), we just check emptiness
        return isEmpty(position)
    }
    
    fun placePinAt(position: Position, pin: Pin): Board {
        require(isEmpty(position)) { "Position $position is already occupied" }
        
        val newSections = Array(3) { sectionRow ->
            Array(3) { sectionCol ->
                if (sectionRow == position.sectionRow && sectionCol == position.sectionCol) {
                    sections[sectionRow][sectionCol].placePinAt(position.localRow, position.localCol, pin)
                } else {
                    sections[sectionRow][sectionCol]
                }
            }
        }
        return Board.fromSections(newSections, seed = this.boardSeed)
    }
    
    /**
     * Remove a pin from the board at the specified position.
     * Used for undo functionality.
     * Does not allow removing dress pins (at positions 3,3 / 3,6 / 6,3 / 6,6).
     * 
     * Requirements: 11.5
     */
    fun removePinAt(position: Position): Board {
        val currentPin = getPinAt(position)
        if (currentPin == null) {
            DebugLogger.d("Board", "removePinAt: No pin at $position")
            return this
        }
        
        // Don't allow removing dress pins (at fixed positions)
        val (legacyRow, legacyCol) = position.toLegacy()
        val dressPinPositions = listOf(
            Pair(3, 3), Pair(3, 6), Pair(6, 3), Pair(6, 6)
        )
        if (Pair(legacyRow, legacyCol) in dressPinPositions) {
            DebugLogger.d("Board", "removePinAt: Cannot remove dress pin at $position")
            return this
        }
        
        val newSections = Array(3) { sectionRow ->
            Array(3) { sectionCol ->
                if (sectionRow == position.sectionRow && sectionCol == position.sectionCol) {
                    sections[sectionRow][sectionCol].removePinAt(position.localRow, position.localCol)
                } else {
                    sections[sectionRow][sectionCol]
                }
            }
        }
        DebugLogger.d("Board", "removePinAt: Removed pin at $position")
        return Board.fromSections(newSections, seed = this.boardSeed)
    }
    
    fun reversePinsAt(positions: List<Position>): Board {
        DebugLogger.d("Board", "REVERSAL: reversePinsAt called with ${positions.size} positions")
        val newSections = Array(3) { sectionRow ->
            Array(3) { sectionCol ->
                sections[sectionRow][sectionCol]
            }
        }
        
        var reversedCount = 0
        for (position in positions) {
            val currentPin = getPinAt(position)
            if (currentPin != null) {
                val reversedPin = currentPin.reversed()
                try {
                    newSections[position.sectionRow][position.sectionCol] = 
                        newSections[position.sectionRow][position.sectionCol]
                            .replacePinAt(position.localRow, position.localCol, reversedPin)
                    reversedCount++
                } catch (e: Exception) {
                    DebugLogger.e("Board", "Pin reversal failed at $position: ${e.message}")
                }
            }
        }
        
        DebugLogger.d("Board", "Successfully reversed $reversedCount pins")
        return Board.fromSections(newSections, seed = this.boardSeed)
    }

    
    fun getOccupiedPositions(): List<Position> {
        val occupied = mutableListOf<Position>()
        for (sectionRow in 0..2) {
            for (sectionCol in 0..2) {
                val section = sections[sectionRow][sectionCol]
                val sectionOccupied = section.getOccupiedPositions()
                for ((localRow, localCol) in sectionOccupied) {
                    occupied.add(Position(sectionRow, sectionCol, localRow, localCol))
                }
            }
        }
        return occupied
    }
    
    fun isFull(): Boolean = getOccupiedPositions().size == 100
    
    fun getPinsOfColor(color: PinColor): List<Position> {
        val positions = mutableListOf<Position>()
        for (sectionRow in 0..2) {
            for (sectionCol in 0..2) {
                val section = sections[sectionRow][sectionCol]
                val sectionPositions = section.getPinsOfColor(color)
                for ((localRow, localCol) in sectionPositions) {
                    positions.add(Position(sectionRow, sectionCol, localRow, localCol))
                }
            }
        }
        return positions
    }
    
    fun getSection(sectionRow: Int, sectionCol: Int): Section {
        require(sectionRow in 0..2 && sectionCol in 0..2) { "Section coordinates must be 0-2" }
        return sections[sectionRow][sectionCol]
    }
    
    fun getAllSections(): Array<Array<Section>> = sections
    
    fun turnJokersBlack(): Board {
        DebugLogger.d("Board", "turnJokersBlack called")
        val newSections = Array(3) { sectionRow ->
            Array(3) { sectionCol ->
                sections[sectionRow][sectionCol]
            }
        }
        
        var jokersConverted = 0
        for (sectionRow in 0..2) {
            for (sectionCol in 0..2) {
                val section = sections[sectionRow][sectionCol]
                val occupiedPositions = section.getOccupiedPositions()
                
                for ((localRow, localCol) in occupiedPositions) {
                    val currentPin = section.getPinAt(localRow, localCol)
                    if (currentPin != null && currentPin.isJoker && !currentPin.isJokerTurnedBlack) {
                        val blackJoker = currentPin.turnJokerBlack()
                        try {
                            newSections[sectionRow][sectionCol] = 
                                newSections[sectionRow][sectionCol].replacePinAt(localRow, localCol, blackJoker)
                            jokersConverted++
                        } catch (e: Exception) {
                            DebugLogger.e("Board", "Joker conversion failed: ${e.message}")
                        }
                    }
                }
            }
        }
        
        DebugLogger.d("Board", "Successfully converted $jokersConverted jokers to black")
        return Board.fromSections(newSections, seed = this.boardSeed)
    }
    
    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null || this::class != other::class) return false
        other as Board
        return sections.contentDeepEquals(other.sections)
    }
    
    override fun hashCode(): Int {
        return sections.contentDeepHashCode()
    }
}
